import { type StockItemSummary } from "@artisancode/api-types";
import { AlertCircle, ArrowDownRight, ArrowUpRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { NumberInput } from "@/components/ui/number-input";

interface Props {
  item: StockItemSummary;
  systemQty: number;
  countedQtyStr: string;
  difference: number;
  onCountedChange: (value: string) => void;
}

/** System balance vs physical count vs difference for the selected item. */
export function StockAdjustmentCountCard({
  item,
  systemQty,
  countedQtyStr,
  difference,
  onCountedChange,
}: Props) {
  return (
    <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
      <div className="grid grid-cols-3 gap-2 text-center items-center">
        <div className="p-2 bg-background rounded-md border">
          <span className="text-[11px] text-muted-foreground block">
            Saldo Sistem
          </span>
          <span className="text-sm font-semibold">
            {systemQty} {item.uomName}
          </span>
        </div>

        <div className="p-2 bg-background rounded-md border">
          <span className="text-[11px] text-muted-foreground block">
            Fisik Dihitung
          </span>
          <NumberInput
            decimals={4}
            showZero
            value={countedQtyStr === "" ? undefined : Number(countedQtyStr)}
            onChange={(n, raw) => onCountedChange(raw === "" ? "" : String(n))}
            placeholder="0"
            className="h-7 text-xs font-semibold text-center mt-0.5"
          />
        </div>

        <div className="p-2 bg-background rounded-md border">
          <span className="text-[11px] text-muted-foreground block">
            Selisih
          </span>
          <div className="mt-1 flex justify-center">
            {difference === 0 ? (
              <Badge variant="outline" className="text-[11px]">
                0 {item.uomName}
              </Badge>
            ) : difference > 0 ? (
              <Badge
                variant="secondary"
                className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20 text-[11px] font-semibold"
              >
                <ArrowUpRight className="h-3 w-3 mr-0.5" />+{difference}{" "}
                {item.uomName}
              </Badge>
            ) : (
              <Badge
                variant="destructive"
                className="text-[11px] font-semibold"
              >
                <ArrowDownRight className="h-3 w-3 mr-0.5" />
                {difference} {item.uomName}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {difference === 0 && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
          <span>Ubah jumlah fisik dihitung untuk mencatat penyesuaian.</span>
        </div>
      )}
    </div>
  );
}
